import { UpperCasePipe } from '@angular/common';
import { Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	ButtonComponent,
	DividerComponent,
	FilePreviewComponent,
	IconComponent,
	LinkComponent,
} from '@tecalisdev/infinity';
import { ViewMediaService } from '../../../../../shared/services/view-media.service';
import { IOperationDetailResponse, OPERATION_TYPE } from '../../../../models/operations.models';
import { OperationsHelperService } from '../../../../services/operations-helper.service';
import { IOperationDocumentsDetail, IOperationOcrDetail } from '../../../../../processes/models/processes.models';
import { collectOcrAdminImageUrls } from '../../../../../processes/utils/ocr-admin-images.util';
import { SnackbarService } from '../../../../../shared/services/snackbar.service';
import { UserActivityTrackerService } from '../../../../../shared/services/user-activity-tracker.service';

@Component({
	selector: 'app-operation-detail-tabs-details-media',
	imports: [
		TranslatePipe,
		UpperCasePipe,
		DividerComponent,
		ButtonComponent,
		IconComponent,
		LinkComponent,
		FilePreviewComponent,
	],
	templateUrl: './operation-detail-tabs-details-media.component.html',
	styleUrl: './operation-detail-tabs-details-media.component.scss',
})
export class OperationDetailTabsDetailsMediaComponent implements OnChanges {
	@Input() detail: IOperationDetailResponse;

	/** URLs en orden de galería: POL → selfies; OCR → anverso, reverso y temporales (sin duplicados). */
	public galleryUrls: string[] = [];

	private viewMedia = inject(ViewMediaService);
	private operationsHelper = inject(OperationsHelperService);
	private trans = inject(TranslateService);
	private snackbarService = inject(SnackbarService);
	private userActivityTracker = inject(UserActivityTrackerService);

	private isDownloading = false;

	public ngOnChanges(changes: SimpleChanges): void {
		if (!changes['detail']) return;
		this.galleryUrls = this.computeGalleryUrls();
	}

	public openGallery(): void {
		if (!this.detail) return;
		if (
			this.detail.operation_type_name === OPERATION_TYPE.OCR ||
			this.detail.operation_type_name === OPERATION_TYPE.DOCUMENTS
		) {
			const payload = this.operationsHelper.buildViewMediaForOperationDetailCard(this.detail);
			if (payload) {
				this.viewMedia.setViewMedia(payload);
			}
			return;
		}
		const first = this.detail.data?.images?.selfie?.[0];
		if (first) this.showViewMedia(first);
	}

	public async downloadMedia(): Promise<void> {
		if (!this.detail || this.isDownloading) {
			return;
		}
		const mediaUrls = this.getOperationMediaUrls();
		if (!mediaUrls.length) {
			return;
		}

		this.isDownloading = true;
		try {
			const downloadPromises = mediaUrls.map((url, index) =>
				this.downloadSingleMedia(url, this.buildMediaFileName(index, url)),
			);
			const results = await Promise.allSettled(downloadPromises);
			const hasErrors = results.some((result) => result.status === 'rejected');
			const hasSuccess = results.some((result) => result.status === 'fulfilled');
			if (hasSuccess) {
				const reference = this.operationsHelper.buildOperationActivityReference(this.detail);
				this.userActivityTracker.track({
					type: 'operations',
					text: `Descarga de imágenes capturadas de la operación ${reference}`,
					link: this.operationsHelper.buildOperationActivityLink(this.detail, reference),
					company_uuid: this.detail?.company_uuid,
				});
			}
			if (hasErrors) {
				this.snackbarService.show(this.trans.instant('ERRORS.GENERIC_DESCRIPTION'));
			}
		} catch (error) {
			console.error(this.trans.instant('ERRORS.GENERIC_DESCRIPTION'), error);
			this.snackbarService.show(this.trans.instant('ERRORS.GENERIC_DESCRIPTION'));
		} finally {
			this.isDownloading = false;
		}
	}

	public showViewMedia(imgSelected: string): void {
		if (!this.detail) return;
		if (
			this.detail.operation_type_name === OPERATION_TYPE.OCR ||
			this.detail.operation_type_name === OPERATION_TYPE.DOCUMENTS
		) {
			const payload = this.operationsHelper.buildViewMediaForOperationDetailCard(this.detail);
			if (!payload) {
				return;
			}
			const hasSelected = payload.data.some((media) => media.doc === imgSelected);
			if (hasSelected) {
				payload.data = payload.data.map((media) => ({
					...media,
					selected: media.doc === imgSelected,
				}));
			}
			this.viewMedia.setViewMedia(payload);
			return;
		}
		this.viewMedia.setViewMedia(this.operationsHelper.buildViewMediaSelfieGallery(this.detail, imgSelected));
	}

	private computeGalleryUrls(): string[] {
		if (!this.detail?.data) {
			return [];
		}
		if (this.detail.operation_type_name === OPERATION_TYPE.OCR) {
			const ocr = this.detail.data as unknown as IOperationOcrDetail;
			const imgs = ocr?.images;
			if (!imgs) {
				return [];
			}
			const urls = collectOcrAdminImageUrls(imgs);
			return Array.from(new Set(urls));
		}
		if (this.detail.operation_type_name === OPERATION_TYPE.DOCUMENTS) {
			const docs = this.detail.data as unknown as IOperationDocumentsDetail;
			return [...(docs?.images?.document ?? [])].filter((url): url is string => Boolean(url));
		}
		return [...(this.detail.data.images?.selfie ?? [])].filter((url): url is string => Boolean(url));
	}

	private getOperationMediaUrls(): string[] {
		const images = this.detail?.data?.images as Record<string, unknown> | undefined;
		if (!images) return [];

		// NIF-1027: ignorar ocr_preview (objeto); solo arrays de URLs.
		const urls = Object.entries(images).flatMap(([key, group]) => {
			if (key === 'ocr_preview' || !Array.isArray(group)) {
				return [];
			}
			return group.filter((url): url is string => typeof url === 'string' && url.length > 0);
		});
		return Array.from(new Set(urls));
	}

	private async downloadSingleMedia(url: string, filename: string): Promise<void> {
		const response = await fetch(url);
		if (!response.ok) throw new Error(`HTTP ${response.status}`);
		const blob = await response.blob();
		const objectUrl = URL.createObjectURL(blob);
		this.triggerDownload(objectUrl, filename);
		URL.revokeObjectURL(objectUrl);
	}

	private triggerDownload(href: string, filename: string): void {
		const anchor = document.createElement('a');
		anchor.href = href;
		anchor.download = filename;
		document.body.appendChild(anchor);
		anchor.click();
		anchor.remove();
	}

	private buildMediaFileName(index: number, url: string): string {
		const opUuid = this.detail?.uuid ?? 'operation';
		const extension = this.getFileExtension(url);
		return `operation-${opUuid}-media-${index + 1}.${extension}`;
	}

	private getFileExtension(url: string): string {
		const cleanUrl = url.split('?')[0].split('#')[0];
		const ext = cleanUrl.split('.').pop()?.toLowerCase();
		if (!ext || ext.length > 5) return 'jpg';
		return ext;
	}
}
